import React, { useMemo } from 'react';
import { Steps } from 'antd';
import { MergedOrder, OrderStatusMap } from '../../../types/process/order';
import type { ColumnsType } from 'antd/es/table';

interface OrderProgressBarProps {
  order: MergedOrder;
}

const statusStepOrder = [1, 3, 5, 11, 13, 15, 17, 23, 25] as const;

type ProgressRowLike = { _rowType?: 'main' | 'progress' };

export const wrapProgressRowRenders = <T extends ProgressRowLike>(
  columns: ColumnsType<T>,
  options: { enabled: boolean; passthroughKeys?: Array<string | number> }
): ColumnsType<T> => {
  if (!options.enabled) return columns;
  const passthrough = new Set(options.passthroughKeys || []);

  const wrap = (cols: ColumnsType<T>): ColumnsType<T> =>
    cols.map((col) => {
      const anyCol = col as any;
      if (anyCol?.children) {
        return { ...col, children: wrap(anyCol.children) };
      }

      const colKey = anyCol?.key;
      if (colKey !== undefined && passthrough.has(colKey)) return col;

      const render = anyCol?.render as ((value: any, record: T, index: number) => any) | undefined;
      return {
        ...col,
        render: (value: any, record: T, index: number) => {
          if ((record as any)?._rowType === 'progress') return { props: { colSpan: 0 } };
          return render ? render(value, record, index) : value;
        },
      };
    });

  return wrap(columns);
};

const OrderProgressBar: React.FC<OrderProgressBarProps> = ({ order }) => {
  const stepItems = useMemo(
    () => statusStepOrder.map((s) => ({ title: OrderStatusMap[s] || String(s) })),
    []
  );

  const status = order.order.orderStatus;
  const current = Math.max(0, statusStepOrder.indexOf(status as any));

  return (
    <Steps
      size="small"
      current={current}
      labelPlacement="vertical"
      items={stepItems as any}
      responsive={false}
    />
  );
};

export default OrderProgressBar;
